import type { TipoAtividade } from './tipos';
import { celulaCsv } from '../../nucleo/csv';

/**
 * CRM no novo desenho, lote 2 (complementos 4 e 5 do clone do RD): regras puras da conclusão com a ata e da "Criar próxima
 * tarefa". Testadas em `apps/web/tests/crm-l2-atividades.test.ts`. Os textos vêm do i18n do módulo (`pipeline:crm.ata.*`).
 */

/** Trecho que a pessoa completa no exemplo de ata. */
export const LACUNA = '[ ]';

/** Posição do primeiro "[ ]" a partir de `desde` (ou do começo, se não houver depois); -1 sem lacuna. */
export function proximaLacuna(texto: string, desde = 0): number {
  const i = texto.indexOf(LACUNA, desde);
  return i >= 0 ? i : texto.indexOf(LACUNA);
}

/** Insere `trecho` na posição do cursor, com quebra de linha quando encosta em texto; devolve o texto e o cursor novo. */
export function inserirNoCursor(texto: string, inicio: number, fim: number, trecho: string): { texto: string; cursor: number } {
  const antes = texto.slice(0, inicio);
  const depois = texto.slice(fim);
  const pre = antes && !/\s$/.test(antes) ? '\n' : '';
  const pos = depois && !/^\s/.test(depois) ? '\n' : '';
  return { texto: antes + pre + trecho + pos + depois, cursor: (antes + pre + trecho).length };
}

/** Acrescenta o exemplo ao que já foi escrito (sem repetir nem apagar o escrito); vazio, o exemplo sozinho. */
export function juntarExemplo(atual: string, exemplo: string) {
  if (!atual.trim()) return exemplo;
  return atual.includes(exemplo) ? atual : `${atual.trim()}\n${exemplo}`;
}

/** O que o CRM sabe da negociação para montar os exemplos e o "Inserir do sistema". */
export interface ContextoAta {
  titulo: string;
  tipo: TipoAtividade;
  /** Data da tarefa já formatada no idioma. */
  data: string;
  contato?: string | null;
  empresa?: string | null;
  telefone?: string | null;
  email?: string | null;
  etapa?: string | null;
}

type T = (chave: string, v?: Record<string, unknown>) => string;

/** Exemplos de ata: o modelo do tipo da tarefa e três situações comuns, já com contato, empresa e telefone. */
export function exemplosDeAta(t: T, c: ContextoAta): { rotulo: string; texto: string }[] {
  const quem = c.contato || c.empresa || t('crm.ata.ocliente');
  const emp = c.empresa || t('crm.ata.aempresa');
  const fone = c.telefone ? t('crm.ata.noTelefone', { fone: c.telefone }) : '';
  const mail = c.email ? ` (${c.email})` : '';
  const v = { quem, emp, fone, mail, titulo: c.titulo, data: c.data, etapa: c.etapa ? t('crm.ata.hojeEm', { etapa: c.etapa }) : '' };
  const modelo = ['ligacao', 'reuniao', 'email', 'whatsapp', 'visita', 'almoco'].includes(c.tipo) ? c.tipo : 'tarefa';
  return [
    { rotulo: t('crm.ata.modeloDe', { tipo: t(`tipoAtividade.${c.tipo}`).toLowerCase() }), texto: t(`crm.ata.modelos.${modelo}`, v) },
    { rotulo: t('crm.ata.situacoes.avancou'), texto: t('crm.ata.textos.avancou', v) },
    { rotulo: t('crm.ata.situacoes.semRetorno'), texto: t('crm.ata.textos.semRetorno', v) },
    { rotulo: t('crm.ata.situacoes.pediuProposta'), texto: t('crm.ata.textos.pediuProposta', v) },
  ];
}

/** Exemplos da conclusão em massa (uma ata para várias tarefas). */
export const exemplosEmMassa = (t: T) => [
  { rotulo: t('crm.ata.situacoes.feito'), texto: t('crm.ata.textos.feito') },
  { rotulo: t('crm.ata.situacoes.semRetorno'), texto: t('crm.ata.textos.semRetornoMassa') },
];

/** Passo de workflow como a API devolve. */
export interface PassoWorkflow {
  quando_tipo: 'qualquer' | TipoAtividade;
  etapa_nome: string | null;
  sugerir_tipo: TipoAtividade;
  assunto: string;
  dias: number;
}
export interface Workflow {
  id: string;
  nome: string;
  funil_id: string | null;
  funil_nome: string | null;
  ativo: boolean;
  passos: PassoWorkflow[];
}

export interface Sugestao {
  tipo: TipoAtividade;
  assunto: string;
  dias: number;
  /** Origem: o passo do workflow ou a palavra da ata. */
  motivo: { chave: 'workflow'; nome: string; passo: number } | { chave: 'ata'; palavra: string } | { chave: 'padrao' };
}

/**
 * Pistas da ata (o clone do RD): palavra no texto da ata, do assunto ou da descrição sugere a próxima tarefa. A ordem é a do
 * clone; o assunto sugerido vem do i18n (`crm.pistas.<chave>`).
 */
export const PISTAS: { chave: string; re: RegExp; tipo: TipoAtividade; dias: number }[] = [
  // Rodada crm1 (SPEC-B5): termos em espanhol também (propuesta, presupuesto, precio, firma, reunión, llamar, volver).
  { chave: 'proposta', re: /proposta|propuesta|or[çc]amento|presupuesto|pre[çc]o|precio|valor|proposal|quote|price/i, tipo: 'email', dias: 1 },
  { chave: 'contrato', re: /contrato|assinatura|\bfirma|fechar|fechamento|cierre|cerrar|aceit|contract|signature/i, tipo: 'tarefa', dias: 1 },
  { chave: 'reuniao', re: /reuni|apresenta|presenta|demonstra|agend|meeting|demo/i, tipo: 'reuniao', dias: 3 },
  { chave: 'documentos', re: /document|cnpj|balancete|contrato social|certid/i, tipo: 'email', dias: 2 },
  { chave: 'retorno', re: /retorn|ligar|liga[çc]|llamar|llamad|volver|pensar|avaliar|evaluar|decis|s[óo]cio|call back/i, tipo: 'ligacao', dias: 2 },
  { chave: 'whatsapp', re: /whats/i, tipo: 'whatsapp', dias: 1 },
];

/**
 * Sugestões da próxima tarefa (até 5): primeiro os passos dos workflows ligados que valem para o funil da negociação, o tipo da
 * tarefa concluída e a etapa; depois as pistas da ata. Sem nenhuma, um follow-up por ligação em 3 dias. Assunto repetido sai.
 */
export function sugestoesPara(
  t: T,
  concluida: { tipo: TipoAtividade; titulo: string; ata?: string | null; descricao?: string | null },
  negociacao: { funil_id?: string | null; etapa_nome?: string | null },
  workflows: Workflow[],
): Sugestao[] {
  const saida: Sugestao[] = [];
  const tem = (assunto: string) => saida.some((s) => s.assunto.toLowerCase() === assunto.toLowerCase());
  for (const w of workflows) {
    if (!w.ativo || (w.funil_id && w.funil_id !== negociacao.funil_id)) continue;
    w.passos.forEach((p, i) => {
      const tipoOk = p.quando_tipo === 'qualquer' || p.quando_tipo === concluida.tipo;
      const etapaOk = !p.etapa_nome || p.etapa_nome.toLowerCase() === (negociacao.etapa_nome ?? '').toLowerCase();
      if (tipoOk && etapaOk && !tem(p.assunto)) saida.push({ tipo: p.sugerir_tipo, assunto: p.assunto, dias: p.dias, motivo: { chave: 'workflow', nome: w.nome, passo: i + 1 } });
    });
  }
  const texto = [concluida.ata, concluida.titulo, concluida.descricao].filter(Boolean).join(' ');
  for (const p of PISTAS) {
    const assunto = t(`crm.pistas.${p.chave}`);
    if (p.re.test(texto) && !tem(assunto)) saida.push({ tipo: p.tipo, assunto, dias: p.dias, motivo: { chave: 'ata', palavra: t(`crm.pistas.palavra.${p.chave}`) } });
  }
  if (!saida.length) saida.push({ tipo: 'ligacao', assunto: t('crm.pistas.padrao'), dias: 3, motivo: { chave: 'padrao' } });
  return saida.slice(0, 5);
}

/** Data e hora da sugestão: daqui a N dias, às 9h no fuso do navegador (valor do campo datetime-local). */
export function dataDaSugestao(dias: number, agora = new Date()): string {
  const d = new Date(agora);
  d.setDate(d.getDate() + dias);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T09:00`;
}

/** Valor do campo datetime-local a partir de um ISO (hora do navegador). */
export function paraLocal(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Modos de "Adiar tarefa" (os do clone) e a data nova de cada um, no fuso do navegador. */
export const MODOS_ADIAR = ['hora1', 'hora2', 'amanha', 'dias2', 'semana', 'mes'] as const;
export type ModoAdiar = (typeof MODOS_ADIAR)[number];
export function adiar(iso: string, modo: ModoAdiar, agora = new Date()): string {
  const d = new Date(iso);
  const base = new Date(Math.max(d.getTime(), agora.getTime()));
  if (modo === 'hora1') return new Date(base.getTime() + 3_600_000).toISOString();
  if (modo === 'hora2') return new Date(base.getTime() + 7_200_000).toISOString();
  if (modo === 'dias2') return new Date(base.getTime() + 2 * 86_400_000).toISOString();
  if (modo === 'amanha') {
    const n = new Date(agora);
    n.setDate(n.getDate() + 1);
    d.setFullYear(n.getFullYear(), n.getMonth(), n.getDate());
    return d.toISOString();
  }
  if (modo === 'semana') d.setDate(d.getDate() + 7);
  else d.setMonth(d.getMonth() + 1);
  return d.toISOString();
}

/** Período da página Atividades (os do clone) como intervalo ISO no fuso do navegador. */
export const PERIODOS = ['hoje', 'semana', 'mes', '7', '14', '30', '180', 'personalizado'] as const;
export type Periodo = (typeof PERIODOS)[number];
export function intervaloDoPeriodo(p: Periodo | '', agora = new Date(), de?: string, ate?: string): { de?: string; ate?: string } {
  const dia0 = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  if (!p) return {};
  if (p === 'hoje') {
    const a = dia0(agora);
    return { de: a.toISOString(), ate: new Date(a.getTime() + 86_400_000 - 1).toISOString() };
  }
  if (p === 'semana') {
    const { de: a, ate: b } = semanaAtual(agora);
    return { de: a, ate: new Date(Date.parse(b) - 1).toISOString() };
  }
  if (p === 'mes') {
    const a = new Date(agora.getFullYear(), agora.getMonth(), 1);
    const b = new Date(agora.getFullYear(), agora.getMonth() + 1, 1);
    return { de: a.toISOString(), ate: new Date(b.getTime() - 1).toISOString() };
  }
  if (p === 'personalizado') {
    return { de: de ? new Date(`${de}T00:00`).toISOString() : undefined, ate: ate ? new Date(`${ate}T23:59:59`).toISOString() : undefined };
  }
  return { de: new Date(agora.getTime() - Number(p) * 86_400_000).toISOString(), ate: agora.toISOString() };
}

/** Semana atual (segunda a domingo) no fuso do navegador: início e fim exclusivo, em ISO. */
export function semanaAtual(agora = new Date()): { de: string; ate: string } {
  const s = new Date(agora);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  const e = new Date(s);
  e.setDate(s.getDate() + 7);
  return { de: s.toISOString(), ate: e.toISOString() };
}

const BOM = String.fromCharCode(0xfeff);

/** CSV da exportação (separador ";" e BOM, como as outras exportações do sistema abrem no Excel). */
export function csv(linhas: (string | number | null | undefined)[][]): string {
  return `${BOM}${linhas.map((l) => l.map(celulaCsv).join(';')).join('\n')}`;
}

/** Proposta como a rota `/pipeline/oportunidades/:id/propostas` devolve (só o que o "Inserir do sistema" usa). */
export interface PropostaDaAta {
  numero: number;
  estado: 'criada' | 'enviada' | 'aceita' | 'recusada';
  /** Lote crm6 (item 18): o status configurado; o de fábrica (nome em pt intacto) sai pelo rótulo do estado. */
  status_nome?: string | null;
  total_centavos: number;
  moeda: string;
  created_at: string;
  /** Nulo sem leitura do Cadastros (rodada crm2, SEG-M1). */
  enviada_para: string | null;
}
/** E-mail como a rota `/pipeline/oportunidades/:id/emails` devolve. */
export interface EmailDaAta {
  /** Nulo sem leitura do Cadastros (rodada crm1 SEG-M3, crm2 SEG-M1): o texto sai sem o endereço. */
  para: string | null;
  assunto: string;
  texto: string;
  created_at: string;
}
const STATUS_FABRICA: Record<PropostaDaAta['estado'], string> = { criada: 'Criada', enviada: 'Enviada', aceita: 'Aceita', recusada: 'Recusada' };
type Fmt = { data: (iso: string) => string; dinheiro: (centavos: number, moeda: string) => string };

const resumo = (texto: string, max = 200) => {
  const t = texto.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
};

/**
 * Rodada crm1 (SPEC-B1): "Última proposta" e "Último e-mail" no "Inserir do sistema" da ata (ficha do novo desenho, lote 2).
 * A proposta mais recente é a de maior número; o e-mail, o enviado por último. O endereço só entra quando a rota o devolve
 * (quem não lê o Cadastros recebe nulo).
 */
export function ultimosDaNegociacao(t: T, fmt: Fmt, propostas: PropostaDaAta[], emails: EmailDaAta[]): { chave: string; rotulo: string; texto: string }[] {
  const infos: { chave: string; rotulo: string; texto: string }[] = [];
  const proposta = [...propostas].sort((a, b) => b.numero - a.numero)[0];
  if (proposta)
    infos.push({
      chave: 'proposta',
      rotulo: t('crm.inserir.ultimaProposta'),
      texto: t('crm.inserir.propostaTxt', {
        numero: proposta.numero,
        estado: (proposta.status_nome && proposta.status_nome !== STATUS_FABRICA[proposta.estado] ? proposta.status_nome : t(`crm.proposta.estados.${proposta.estado}`)).toLowerCase(),
        data: fmt.data(proposta.created_at),
        valor: fmt.dinheiro(proposta.total_centavos, proposta.moeda),
        para: proposta.enviada_para ? t('crm.inserir.enviadaPara', { email: proposta.enviada_para }) : '',
      }),
    });
  const email = [...emails].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  if (email)
    infos.push({
      chave: 'email',
      rotulo: t('crm.inserir.ultimoEmail'),
      texto: t('crm.inserir.emailTxt', {
        data: fmt.data(email.created_at),
        para: email.para ? t('crm.inserir.paraEmail', { email: email.para }) : '',
        assunto: email.assunto,
        texto: resumo(email.texto),
      }),
    });
  return infos;
}
